import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { alpha } from '@mui/material/styles';

// Icons
import SecurityIcon from '@mui/icons-material/Security';
import StarIcon from '@mui/icons-material/Star';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// Import components
import HeroSection from '../components/home/HeroSection';
import FleetPreview from '../components/home/FleetPreview';
import TestimonialSection from '../components/home/TestimonialSection';
import ServiceHighlights from '../components/home/ServiceHighlights';
import GridContainer from '../components/common/GridContainer';
import GridItem from '../components/common/GridItem';
import SEO from '../components/common/SEO';
import { pageSEO, generateStructuredData } from '../utils/seo';

const features = [
  {
    icon: <SecurityIcon fontSize="large" />,
    title: 'Safety & Security',
    description:
      'We maintain the highest safety standards with professionally trained chauffeurs and regularly serviced vehicles.',
  },
  {
    icon: <StarIcon fontSize="large" />,
    title: 'Premium Service',
    description:
      'Experience luxury transportation with attention to detail. We cater to your specific needs with personalized service.',
  },
  {
    icon: <AccessTimeIcon fontSize="large" />,
    title: 'Reliability',
    description:
      'Punctuality is our promise. We monitor traffic and flight statuses to ensure on-time arrivals and departures.',
  },
  {
    icon: <LocalOfferIcon fontSize="large" />,
    title: 'Corporate Packages',
    description:
      'Custom solutions for businesses of all sizes. Manage your corporate transportation with dedicated account management.',
  },
];

const benefits = [
  'Dedicated account manager for corporate clients',
  'Secure online billing portal',
  'Fleet management and analytics',
  '24/7 customer support',
  'Global transportation solutions',
  'Custom reporting and cost-saving strategies',
];

const HomePage: React.FC = () => {
  const theme = useTheme();
  const seoData = pageSEO['/'];
  const structuredData = generateStructuredData('/');

  // Common section title styling
  const sectionTitleStyles = {
    textAlign: 'center',
    mb: { xs: 6, md: 8 },
    mx: 'auto',
    maxWidth: 900
  };

  // Common section subtitle styling
  const sectionSubtitleStyles = {
    color: 'secondary.main',
    fontWeight: 600,
    mb: 1,
    letterSpacing: 1,
    textTransform: 'uppercase',
    textAlign: 'center'
  };

  // Common section heading styling
  const sectionHeadingStyles = {
    fontFamily: 'Playfair Display, serif',
    fontWeight: 600,
    mb: 3,
    fontSize: { xs: '2.25rem', md: '3rem' },
    textAlign: 'center'
  };

  // Common section description styling
  const sectionDescriptionStyles = {
    color: 'text.secondary',
    maxWidth: 800,
    mx: 'auto',
    fontSize: { xs: '1rem', md: '1.1rem' },
    lineHeight: 1.7,
    textAlign: 'center'
  };

  return (
    <>
      <SEO 
        pathname="/" 
        {...seoData} 
        structuredData={structuredData}
      />
      <Box
        sx={{
          minHeight: '100vh',
          background: `
          linear-gradient(135deg, 
            ${alpha(theme.palette.primary.main, 0.05)} 0%, 
            ${alpha(theme.palette.secondary.main, 0.05)} 100%
          ),
          radial-gradient(
            circle at 50% 50%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 0% 100%,
            ${alpha(theme.palette.secondary.light, 0.1)} 0%,
            transparent 50%
          ),
          radial-gradient(
            circle at 100% 0%,
            ${alpha(theme.palette.primary.light, 0.1)} 0%,
            transparent 50%
          )
        `,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(
              circle at 50% 50%,
              ${alpha(theme.palette.primary.main, 0.03)} 0%,
              transparent 70%
            )
          `,
          animation: 'pulse 8s ease-in-out infinite',
          '@keyframes pulse': {
            '0%': {
              transform: 'scale(1)',
              opacity: 1,
            },
            '50%': {
              transform: 'scale(1.2)',
              opacity: 0.8,
            },
            '100%': {
              transform: 'scale(1)',
              opacity: 1,
            },
          },
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            linear-gradient(
              45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
            ),
            linear-gradient(
              -45deg,
              transparent 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 48%,
              ${alpha(theme.palette.primary.main, 0.03)} 52%,
              transparent 52%
          )`,
          backgroundSize: '60px 60px',
          opacity: 0.5,
          animation: 'move 20s linear infinite',
          '@keyframes move': {
            '0%': {
              backgroundPosition: '0 0',
            },
            '100%': {
              backgroundPosition: '60px 60px',
            },
          },
        }}
      />
      {/* Hero Section */}
      <HeroSection />
      
      {/* Core Features */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          backgroundColor: '#fff',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={sectionTitleStyles}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <Typography
                variant="h6"
                component="p"
                sx={sectionSubtitleStyles}
              >
                WHY CHOOSE US
              </Typography>
              <Typography
                variant="h3"
                component="h2"
                sx={sectionHeadingStyles}
              >
                Premium Transportation Solutions
              </Typography>
              <Typography
                variant="body1"
                sx={sectionDescriptionStyles}
              >
                We provide exceptional corporate transportation services with a focus on reliability,
                luxury, and customer satisfaction.
              </Typography>
            </motion.div>
          </Box>

          <GridContainer spacing={4}>
            {features.map((feature, index) => (
              <GridItem 
                xs={12} 
                sm={6} 
                md={3} 
                key={index}
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  style={{ height: '100%' }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid',
                      borderColor: 'divider',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
                        transform: 'translateY(-5px)',
                        borderColor: 'transparent',
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        p: 3,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 2,
                          width: 70,
                          height: 70,
                          borderRadius: '50%',
                          color: 'primary.main',
                          bgcolor: 'rgba(26, 43, 60, 0.05)',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        component="h3"
                        sx={{ mb: 1, fontWeight: 600 }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </GridItem>
            ))}
          </GridContainer>
        </Container>
      </Box>

      {/* Service Highlights */}
      <ServiceHighlights />

      {/* Fleet Preview */}
      <FleetPreview />

      {/* Testimonials */}
      <TestimonialSection />

      {/* Corporate Solutions */}
      <Box
        sx={{
          py: { xs: 6, md: 10 },
          bgcolor: 'grey.50',
        }}
      >
        <Container maxWidth="lg">
          <GridContainer spacing={4} alignItems="center">
            <GridItem xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    color: 'secondary.main',
                    fontWeight: 600,
                    mb: 1,
                    letterSpacing: 1,
                  }}
                >
                  CORPORATE SOLUTIONS
                </Typography>
                <Typography
                  variant="h3"
                  component="h2"
                  sx={{
                    fontFamily: 'Playfair Display, serif',
                    fontWeight: 600,
                    mb: 3,
                    fontSize: { xs: '2rem', md: '2.5rem' },
                  }}
                >
                  Elevate Your Business Transportation
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    mb: 4,
                    fontSize: { xs: '1rem', md: '1.1rem' },
                    lineHeight: 1.7,
                  }}
                >
                  Our corporate transportation services are designed to meet the unique needs of
                  businesses. From airport transfers to executive travel and event logistics, we provide
                  reliable and professional service that reflects your company's values and standards.
                </Typography>

                <Box sx={{ mb: 4 }}>
                  {benefits.map((benefit, index) => (
                    <Box
                      key={index}
                      sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
                    >
                      <CheckCircleIcon
                        sx={{ mr: 2, color: 'secondary.main' }}
                      />
                      <Typography variant="body1">{benefit}</Typography>
                    </Box>
                  ))}
                </Box>

                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Button
                    component={RouterLink}
                    to="/corporate"
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '1rem',
                    }}
                  >
                    Corporate Solutions
                  </Button>
                </motion.div>
              </motion.div>
            </GridItem>
            <GridItem xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.7 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="/images/corporate-solution.jpg"
                  alt="Corporate Transportation Solution"
                  sx={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: 3,
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
                  }}
                />
              </motion.div>
            </GridItem>
          </GridContainer>
        </Container>
      </Box>
    </Box>
    </>
  );
};

export default HomePage; 